import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getVenueToken, venueHeaders, formatApiError } from "@/lib/api";
import { BrainTapLogo } from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2, Brain, Loader2, XCircle } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-navy-900 px-4 text-cream"
      style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,180,60,0.12), transparent 50%), #190a20" }}
      data-testid="payment-page"
    >
      <Link to="/" className="flex items-center gap-3">
        <BrainTapLogo mascotClass="h-11" lockupClass="h-8" />
      </Link>
      {children}
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const triesRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return undefined;
    }
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          if (getVenueToken()) {
            try {
              await api.post("/billing/verify", { session_id: sessionId }, { headers: venueHeaders() });
            } catch (e) { /* room tied at checkout */ }
          }
          setStatus("paid");
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    };
    const iv = setInterval(async () => {
      triesRef.current += 1;
      const done = await poll();
      if (done || triesRef.current > 40) {
        clearInterval(iv);
        if (alive && !done) setStatus("timeout");
      }
    }, 2500);
    poll().then((done) => { if (done) clearInterval(iv); });
    return () => { alive = false; clearInterval(iv); };
  }, [sessionId]);

  if (status === "checking") {
    return (
      <Shell>
        <Loader2 size={36} className="animate-spin text-amber" />
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-mut">Confirming your subscription…</p>
      </Shell>
    );
  }

  if (status === "paid") {
    return (
      <Shell>
        <div className="glass w-full max-w-md rounded-3xl p-10 text-center">
          <CheckCircle2 size={56} className="mx-auto mb-5 text-mint" />
          <h1 className="mb-3 font-display text-3xl font-extrabold uppercase tracking-tight">You're subscribed!</h1>
          <p className="mb-8 text-mut">Your room is on the plan. The big screen, specials and leaderboards are all yours.</p>
          <Link to="/venue" data-testid="payment-back-button" className="inline-flex items-center gap-2 rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
            <Brain size={17} /> Back to my venue
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="glass w-full max-w-md rounded-3xl p-10 text-center">
        <XCircle size={56} className="mx-auto mb-5 text-coral" />
        <h1 className="mb-3 font-display text-3xl font-extrabold uppercase tracking-tight">We couldn't confirm that</h1>
        <p className="mb-8 text-mut">If your card was charged, give it a minute and check the venue dashboard.</p>
        <Link to="/venue" className="inline-flex rounded-full border-2 border-ice/25 px-8 py-3.5 font-display font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber">
          Go to dashboard
        </Link>
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div className="glass w-full max-w-md rounded-3xl p-10 text-center">
        <XCircle size={56} className="mx-auto mb-5 text-mut" />
        <h1 className="mb-3 font-display text-3xl font-extrabold uppercase tracking-tight">Checkout canceled</h1>
        <p className="mb-8 text-mut">No charge was made. Your free trial keeps running.</p>
        <Link to="/venue" data-testid="payment-cancel-back" className="inline-flex rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">
          Back to dashboard
        </Link>
      </div>
    </Shell>
  );
}
